// src/pages/PaymentSuccess.js
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiDownload, FiPlay, FiShare2, FiClock } from 'react-icons/fi';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../utils/apiClient';
import LinkedInShareButton from '../components/LinkedInShareButton';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 48px;
  max-width: 600px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const SuccessIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #28a745, #20c997);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: white;
  font-size: 40px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const CourseInfo = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 16px;
  padding: 24px;
  margin: 32px 0;
  text-align: left;
`;

const CourseName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
`;

const CourseDetails = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 16px;
`;

const PaymentDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
`;

const DetailLabel = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

const DetailValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  font-weight: 600;
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin: 32px 0;
`;

const ActionButton = styled.button`
  padding: 16px 24px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &.primary {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
    }
  }
  
  &.secondary {
    background: ${({ theme }) => theme.colors.backgroundLight};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.divider};
    
    &:hover {
      background: ${({ theme }) => theme.colors.background};
      transform: translateY(-2px);
    }
  }
`;

const ShareSection = styled.div`
  background: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 16px;
  padding: 24px;
  margin: 32px 0;
`;

const ShareTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NextSteps = styled.div`
  text-align: left;
  background: #e7f3ff;
  border: 1px solid #007bff;
  border-radius: 12px;
  padding: 24px;
  margin: 32px 0;
`;

const NextStepsTitle = styled.h4`
  color: #007bff;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepsList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: ${({ theme }) => theme.colors.text};
`;

const StepItem = styled.li`
  margin-bottom: 8px;
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const orderId = searchParams.get('order_id');
  const paymentId = searchParams.get('payment_id');

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      // This would be a new endpoint to get order details
      const response = await api.payment.getOrderDetails(orderId);
      setOrderData(response.data);
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = async () => {
    try {
      await api.payment.downloadInvoice(orderId);
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  const formatAmount = (amount) => {
    return `₹${amount.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Container>
        <Card
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <LoadingContainer>
            Loading order details...
          </LoadingContainer>
        </Card>
      </Container>
    );
  }

  if (!orderData) {
    return (
      <Container>
        <Card
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Title>Order Not Found</Title>
          <Subtitle>We couldn't find the order details. Please check your purchase history.</Subtitle>
          <Link to="/purchase-history">
            <ActionButton className="primary">
              View Purchase History
            </ActionButton>
          </Link>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <SuccessIcon
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
        >
          <FiCheckCircle />
        </SuccessIcon>

        <Title>Payment Successful!</Title>
        <Subtitle>
          Congratulations! Your payment has been processed successfully. 
          You now have lifetime access to your course.
        </Subtitle>

        <CourseInfo>
          <CourseName>{orderData.course_name}</CourseName>
          <CourseDetails>
            You've successfully enrolled in this course. Start learning immediately!
          </CourseDetails>
          
          <PaymentDetails>
            <DetailItem>
              <DetailLabel>Order ID</DetailLabel>
              <DetailValue>{orderData.order_id}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Amount Paid</DetailLabel>
              <DetailValue>{formatAmount(orderData.amount)}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Payment ID</DetailLabel>
              <DetailValue>{paymentId}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Date</DetailLabel>
              <DetailValue>{formatDate(orderData.created_at)}</DetailValue>
            </DetailItem>
          </PaymentDetails>
        </CourseInfo>

        <ActionsGrid>
          <Link to={`/courses/${orderData.course_id}`}>
            <ActionButton className="primary">
              <FiPlay />
              Start Learning
            </ActionButton>
          </Link>
          
          <ActionButton className="secondary" onClick={downloadInvoice}>
            <FiDownload />
            Download Invoice
          </ActionButton>
        </ActionsGrid>

        <ShareSection>
          <ShareTitle>
            <FiShare2 />
            Share Your Achievement
          </ShareTitle>
          <LinkedInShareButton 
            courseTitle={orderData.course_name}
            message="I just enrolled in a cybersecurity course on CHIEF platform! 🚀"
          />
        </ShareSection>

        <NextSteps>
          <NextStepsTitle>
            <FiClock />
            What's Next?
          </NextStepsTitle>
          <StepsList>
            <StepItem>Access your course dashboard and explore the curriculum</StepItem>
            <StepItem>Start with the first module and track your progress</StepItem>
            {orderData.has_lab_access && (
              <StepItem>Access VM labs for hands-on practice (valid for 1 year)</StepItem>
            )}
            <StepItem>Complete quizzes to test your knowledge</StepItem>
            <StepItem>Earn certificates upon course completion</StepItem>
          </StepsList>
        </NextSteps>

        <p style={{ 
          fontSize: '14px', 
          color: '#6c757d', 
          marginTop: '32px',
          marginBottom: '0'
        }}>
          If you have any questions or need support, please contact our team at support@chief-platform.com
        </p>
      </Card>
    </Container>
  );
};

export default PaymentSuccess;
